import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createTask, getMyPostedTasks } from '../../api/taskAPI';
import { Briefcase, Plus, ChevronRight, Users } from 'lucide-react';

const CATEGORIES = [
  { value: 'home-based', label: 'Home-Based' },
  { value: 'part-time', label: 'Part-Time' },
  { value: 'freelance', label: 'Freelancing' },
  { value: 'local-service', label: 'Local Services' },
  { value: 'internship', label: 'Internship' },
];

const STATUS_MAP = {
  open: 'badge-active',
  closed: 'badge-closed',
  'in-progress': 'badge-brand',
  pending: 'badge-pending',
};

const Provider = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    jobTitle: '', category: 'home-based', description: '',
    location: '', duration: '', payment: '', skills: '', workType: 'full-time',
  });
  const [postedJobs, setPostedJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [loadingJobs, setLoadingJobs] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetch = async () => {
      setLoadingJobs(true);
      try { const r = await getMyPostedTasks(); setPostedJobs(r.data || []); }
      catch {} finally { setLoadingJobs(false); }
    };
    fetch();
  }, [isAuthenticated]);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault(); setFormError(''); setFormSuccess(''); setSubmitting(true);
    try {
      const dp = formData.duration.trim().split(' ');
      const res = await createTask({
        title: formData.jobTitle, category: formData.category, description: formData.description,
        location: { city: formData.location, isRemote: formData.location.toLowerCase() === 'remote' },
        duration: { value: parseInt(dp[0]) || 1, unit: ['hours', 'days', 'weeks', 'months'].includes(dp[1]?.toLowerCase()) ? dp[1].toLowerCase() : 'days' },
        budget: { amount: parseFloat(formData.payment.replace(/[^0-9.]/g, '')) || 0, currency: 'INR', type: 'fixed' },
        skillsRequired: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setPostedJobs((p) => [res.data, ...p]);
      setFormSuccess('Job posted! Workers can now apply.');
      setFormData({ jobTitle: '', category: 'home-based', description: '', location: '', duration: '', payment: '', skills: '', workType: 'full-time' });
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Failed to post job');
    } finally { setSubmitting(false); }
  };

  const glassStyle = {
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderColor: 'rgba(255,255,255,0.15)'
  };

  return (
    <div className="min-h-screen pt-20 text-white" style={{
      backgroundImage: "url('/images/team/post.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      '--foreground': '#ffffff',
      '--muted-foreground': '#f4f4f5',
      '--card-foreground': '#ffffff',
      '--border': 'rgba(255, 255, 255, 0.15)',
      '--accent': 'rgba(255, 255, 255, 0.08)',
      '--muted': 'rgba(255, 255, 255, 0.05)',
    }}>

      {/* Page Header */}
      <div className="border-b glass-panel" style={glassStyle}>
        <div className="section-container py-8">
          <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Post a Job</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Find the right talent for your work</p>
        </div>
      </div>

      <div className="section-container py-8">
        <div className="grid lg:grid-cols-5 gap-8">

          {/* Posted Jobs Sidebar */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between glass-panel px-4 py-2 rounded-xl" style={glassStyle}>
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Your Listings</h2>
              {!loadingJobs && <span className="text-xs font-medium px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)' }}>{postedJobs.length} total</span>}
            </div>

            {!isAuthenticated && (
              <div className="rounded-2xl border p-8 text-center glass-panel" style={glassStyle}>
                <p className="text-sm mb-4" style={{ color: 'var(--foreground)' }}>Sign in to view and manage your job listings.</p>
                <a href="/login" className="btn-primary text-sm">Sign In</a>
              </div>
            )}

            {isAuthenticated && loadingJobs && (
              <div className="flex justify-center py-12 glass-panel rounded-2xl" style={glassStyle}>
                <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--foreground)' }} />
              </div>
            )}

            {isAuthenticated && !loadingJobs && postedJobs.length === 0 && (
              <div className="rounded-2xl border p-10 text-center glass-panel" style={glassStyle}>
                <Briefcase className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--foreground)', opacity: 0.7 }} />
                <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>No listings yet</p>
                <p className="text-xs" style={{ color: 'var(--foreground)' }}>Create your first job posting using the form.</p>
              </div>
            )}

            <div className="space-y-3">
              {postedJobs.map((job) => {
                const statusKey = job.status || 'open';
                return (
                  <div key={job._id} className="rounded-2xl border p-5 transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer glass-panel"
                    style={glassStyle}
                    onClick={() => navigate(`/job/${job._id}`)}>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <p className="font-bold text-sm leading-snug" style={{ color: 'var(--foreground)' }}>{job.title}</p>
                      <span className={STATUS_MAP[statusKey] || 'badge-closed'}>{statusKey}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs mb-3" style={{ color: 'var(--foreground)' }}>
                      {job.budget?.amount > 0 && <span className="font-semibold" style={{ color: 'var(--foreground)' }}>₹{job.budget.amount}</span>}
                      {job.location?.city && <span>{job.location.city}</span>}
                      {job.category && <span className="capitalize">{job.category}</span>}
                    </div>
                    {job.skillsRequired?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {job.skillsRequired.slice(0, 3).map((s) => <span key={s} className="badge-outline text-xs" style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'var(--foreground)' }}>{s}</span>)}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                      <p className="text-xs flex items-center gap-1" style={{ color: 'var(--foreground)' }}>
                        <Users className="w-3.5 h-3.5" />
                        {Array.isArray(job.applications) && job.applications.length > 0
                          ? `${job.applications.length} application${job.applications.length !== 1 ? 's' : ''}`
                          : 'No applications yet'}
                      </p>
                      <span className="text-xs flex items-center gap-1" style={{ color: 'var(--foreground)' }}>
                        View <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Job Posting Form */}
          <section className="lg:col-span-3">
            <div className="rounded-2xl border overflow-hidden glass-panel" style={glassStyle}>
              <div className="px-6 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
                  <Plus className="w-4 h-4" style={{ color: 'var(--primary-foreground)' }} />
                </div>
                <div>
                  <h2 className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>Create New Listing</h2>
                  <p className="text-xs" style={{ color: 'var(--foreground)' }}>Fill in the details to attract the right candidates</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Job Title</label>
                    <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleInputChange}
                      required placeholder="e.g. Content Writer" className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Category</label>
                    <select name="category" value={formData.category} onChange={handleInputChange} className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }}>
                      {CATEGORIES.map(({ value, label }) => <option key={value} value={value} style={{ color: 'black' }}>{label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label-text" style={{ color: 'var(--foreground)' }}>Description</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange}
                    required rows={4} placeholder="Describe the work, requirements, and expectations..."
                    className="input-field resize-none" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleInputChange}
                      required placeholder='City or "Remote"' className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Duration</label>
                    <input type="text" name="duration" value={formData.duration} onChange={handleInputChange}
                      required placeholder="e.g. 2 months" className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Budget (INR)</label>
                    <input type="text" name="payment" value={formData.payment} onChange={handleInputChange}
                      required placeholder="e.g. 15000" className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <div>
                    <label className="label-text" style={{ color: 'var(--foreground)' }}>Work Type</label>
                    <select name="workType" value={formData.workType} onChange={handleInputChange} className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }}>
                      <option value="full-time" style={{ color: 'black' }}>Full-Time</option>
                      <option value="part-time" style={{ color: 'black' }}>Part-Time</option>
                      <option value="contract" style={{ color: 'black' }}>Contract</option>
                      <option value="remote" style={{ color: 'black' }}>Remote</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label-text" style={{ color: 'var(--foreground)' }}>
                    Skills Required <span className="font-normal text-xs ml-1" style={{ color: 'rgba(255,255,255,0.7)' }}>(comma-separated)</span>
                  </label>
                  <input type="text" name="skills" value={formData.skills} onChange={handleInputChange}
                    placeholder="e.g. Communication, MS Excel, Driving" className="input-field" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--foreground)', borderColor: 'rgba(255,255,255,0.2)' }} />
                </div>

                {formError && (
                  <div className="px-4 py-3 rounded-xl text-sm border glass-panel" style={{ color: '#ff8a8a', background: 'rgba(255,0,0,0.1)', borderColor: 'rgba(255,0,0,0.2)' }}>
                    {formError}
                  </div>
                )}
                {formSuccess && (
                  <div className="px-4 py-3 rounded-xl text-sm border glass-panel" style={{ color: '#4ade80', background: 'rgba(0,255,0,0.1)', borderColor: 'rgba(0,255,0,0.2)' }}>
                    {formSuccess}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setFormData({ jobTitle: '', category: 'home-based', description: '', location: '', duration: '', payment: '', skills: '', workType: 'full-time' })}
                    className="btn-ghost text-sm" style={{ color: 'var(--foreground)' }}>Clear</button>
                  <button type="submit" disabled={submitting} className="btn-primary" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    {submitting ? 'Posting...' : 'Post Job'}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Provider;